<template>
  <span
    :class="[
      'toggle-fi',
      variant ? `toggle-fi--${variant}` : null,
      disabled ? 'toggle-fi--disabled' : null,
      label ? 'toggle-fi--with-label' : null,
    ]"
    @click="clickHandler"
  >
    <span
      :class="[
        'toggle-fi__toggle',
        isChecked ? 'toggle-fi__toggle--checked' : 'toggle-fi__toggle--unchecked',
      ]"
      :tabindex="tabindex"
      :autofocus="autofocus"
      :aria-checked="isChecked"
      role="checkbox"
      @blur="blurHandler"
      @focus="focusHandler"
      @keydown="onWrapperKeydown"
    >
      <input
        :id="id"
        ref="input"
        :value="currentValue"
        :name="name"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        type="hidden"
      />
      <span
        class="toggle-fi__checked-placeholder"
        aria-hidden
      />
      <span
        class="toggle-fi__unchecked-placeholder"
        aria-hidden
      />
      <span
        ref="button"
        :class="isChecked ? 'toggle-fi__button--checked' : 'toggle-fi__button'"
        aria-hidden
      />
    </span>

    <label
      v-if="label"
      :for="id"
    >
      {{ label }}
    </label>
  </span>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';

import Key from '../checkbox-fi/Key';

defineOptions({ name: 'ToggleFi' });

type ModelValue = string | object | number | boolean | any[];

interface Props {
  modelValue?: ModelValue;
  value?: ModelValue;
  disabled?: boolean;
  readonly?: boolean;
  id?: string;
  name?: string;
  uncheckedValue?: ModelValue;
  autofocus?: boolean;
  required?: boolean;
  tabindex?: string | number;
  variant?: string;
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  value: true,
  disabled: undefined,
  readonly: undefined,
  id: undefined,
  name: undefined,
  uncheckedValue: false,
  autofocus: undefined,
  required: undefined,
  tabindex: undefined,
  variant: undefined,
  label: undefined,
});

const emit = defineEmits<{
  blur: [e: FocusEvent];
  change: [value: ModelValue];
  focus: [e: FocusEvent];
  input: [value: any];
  'update:modelValue': [value: any];
}>();

const input = useTemplateRef<HTMLInputElement>('input');
const fallback = ref(false);

const isChecked = computed<boolean>(() => {
  if (props.modelValue === undefined) {
    return fallback.value;
  }
  if (Array.isArray(props.modelValue)) {
    return props.modelValue.includes(props.value);
  }
  return props.modelValue === props.value;
});

watch(isChecked, (value) => {
  if (value) {
    emit('change', props.value);
  }
});

const click = () => {
  if (input.value) {
    input.value.click();
  } else {
    console.error('missing toggle element to click');
  }
};

const blurHandler = (e: FocusEvent) => {
  emit('blur', e);
};

const focusHandler = (e: FocusEvent) => {
  emit('focus', e);
};

watch(
  () => fallback.value,
  (newValue) => {
    emit('input', newValue);
  },
);

watch(
  () => props.modelValue,
  (newValue) => {
    emit('input', newValue);
  },
);

const toggle = () => {
  if (props.disabled) {
    return;
  }
  if (props.modelValue === undefined) {
    fallback.value = !fallback.value;
    emit('change', fallback.value);
    return;
  }

  let localValue;
  if (Array.isArray(props.modelValue)) {
    localValue = [...props.modelValue];
    if (isChecked.value) {
      localValue = props.modelValue.filter((value) => value !== props.value);
    } else if (!props.modelValue.includes(props.value)) {
      localValue = [...props.modelValue, props.value];
    }
  } else {
    localValue = Boolean(isChecked.value ? props.uncheckedValue : props.value);
  }

  emit('update:modelValue', localValue);
};

const clickHandler = (evt: MouseEvent) => {
  if (evt?.detail !== 1) {
    return;
  }
  toggle();
};

const onWrapperKeydown = (e: KeyboardEvent) => {
  if (e.keyCode === Key.SPACE) {
    e.preventDefault();
    click();
  }
};

const currentValue = computed(() => (isChecked.value ? props.value : props.uncheckedValue));

defineExpose({ click, blur: () => input.value?.blur(), focus: () => input.value?.focus() });
</script>

<style scoped src="./toggle-fi" />
